import { ReactNode, Children, isValidElement } from 'react';
import styled from 'styled-components';
import { WithPopoverPlacement } from './types';
import {
  WithUncontrolledPopover,
  PlainTextPopover,
  popoverUnderline,
  type ChildProps,
  type PopoverPlacement,
} from './popover2025';

// We want to exclude inline JSX, as that is not rendered lazily on-demand
export type PopoverContentType =
  | (() => ReactNode)
  | string
  | number
  | false
  | null
  | undefined;

type WithPopoverOwnProps = {
  content: PopoverContentType;
  children: ReactNode;
};

export type WithPopoverProps = WithPopoverOwnProps & {
  /**
   * Where to place the popover. Accepts the legacy top/bottom enum, or a full
   * `{ side, overflow }` placement to also express the left/right sides.
   */
  preferredPlacement?: WithPopoverPlacement | PopoverPlacement;
  acceptsMouse?: boolean;
  /** Also open the popover when the anchor receives keyboard focus. */
  openOnFocus?: boolean;
};

/**
 * Used to automatically apply a dotted underline to plain-text children
 */
const PopoveredText = styled.span`
  ${popoverUnderline}
`;

const placementToSide = (
  placement?: WithPopoverPlacement
): 'top' | 'bottom' => {
  if (WithPopoverPlacement.TOP === placement) {
    return 'top';
  }
  return 'bottom';
};

const renderContent = (content: PopoverContentType): ReactNode => {
  if (typeof content === 'string' || typeof content === 'number') {
    return <PlainTextPopover>{content}</PlainTextPopover>;
  }
  if (typeof content === 'function') {
    return content();
  }
  return null;
};

export const WithPopover = ({
  content,
  preferredPlacement,
  acceptsMouse,
  openOnFocus,
  children,
}: WithPopoverProps) => {
  if (!content) {
    return <>{children}</>;
  }

  const resolvedPlacement: PopoverPlacement =
    typeof preferredPlacement === 'object'
      ? preferredPlacement
      : { side: placementToSide(preferredPlacement), overflow: 'center' };
  const PopoverContent = () => {
    const rendered = renderContent(content);
    return rendered ? <>{rendered}</> : null;
  };

  const kiddos = Children.map(children, child => {
    if (!child) {
      return;
    }
    if (isValidElement(child)) {
      return (
        <WithUncontrolledPopover
          content={PopoverContent}
          placement={resolvedPlacement}
          keepOpenOnPopoverHover={acceptsMouse}
          openOnFocus={openOnFocus}
        >
          {child as React.ReactElement<ChildProps>}
        </WithUncontrolledPopover>
      );
    }
    // A simple value (eg. string, number) needs something which can have props
    return (
      <WithUncontrolledPopover
        content={PopoverContent}
        placement={resolvedPlacement}
        keepOpenOnPopoverHover={acceptsMouse}
        openOnFocus={openOnFocus}
      >
        <PopoveredText>{child}</PopoveredText>
      </WithUncontrolledPopover>
    );
  });
  return <>{kiddos}</>;
};
